import { useEffect, useState } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import {
  CheckCircle2,
  PackageCheck,
  Truck,
  ShoppingBag,
  Copy,
  Check,
  ArrowRight,
  ArrowLeft,
  Clock,
  ShieldCheck,
  HelpCircle,
  Banknote,
  CreditCard,
  ExternalLink,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import * as ordersApi from "../api/ordersApi";
import { formatMoney } from "../utils/format";
import "./Thanks.css";

export default function Thanks({ onCartUpdated }) {
  const { t, isRTL, language } = useLanguage();
  const { userId, isAuthenticated } = useAuth();
  const { clearGuestCart } = useCart();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const orderId = searchParams.get("orderId") || searchParams.get("id") || location.state?.orderId || "";
  const paymentMethod = searchParams.get("method") || location.state?.paymentMethod || "CashOnDelivery";

  const [copied, setCopied] = useState(false);
  const [order, setOrder] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);

  // Set document title & refresh cart states on arrival
  useEffect(() => {
    document.title = t("thanks.metaTitle");
    if (!isAuthenticated) {
      clearGuestCart && clearGuestCart();
    }
    if (onCartUpdated) {
      onCartUpdated();
    }
  }, [t, isAuthenticated, clearGuestCart, onCartUpdated]);

  // Optionally fetch order details if user is authenticated and orderId is available
  useEffect(() => {
    if (!orderId || !userId || !isAuthenticated) return;
    let cancelled = false;
    setLoadingOrder(true);
    ordersApi
      .getOrder(userId, orderId)
      .then((data) => {
        if (!cancelled && data) {
          setOrder(data);
        }
      })
      .catch((err) => {
        // Non-critical: failure to fetch order details simply leaves generic confirmation
        console.debug("Could not pre-fetch order summary:", err);
      })
      .finally(() => {
        if (!cancelled) setLoadingOrder(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orderId, userId, isAuthenticated]);

  const handleCopyOrderId = () => {
    if (!orderId) return;
    navigator.clipboard.writeText(orderId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    });
  };

  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  return (
    <div className="thanks-page">
      <div className="container thanks-inner">
        {/* Hero Confirmation Card */}
        <section className="thanks-hero-card">
          <div className="thanks-hero__seal">
            <div className="thanks-hero__icon-wrap">
              <CheckCircle2 size={46} strokeWidth={2.2} className="thanks-hero__check-icon" />
            </div>
            <div className="thanks-hero__glow" />
          </div>

          <span className="thanks-hero__eyebrow">{t("brand.name")}</span>
          <h1 className="thanks-hero__title">{t("thanks.title")}</h1>
          <p className="thanks-hero__subtitle">{t("thanks.subtitle")}</p>

          {/* Order Reference Box (if available) */}
          {orderId && (
            <div className="thanks-ref-box">
              <div className="thanks-ref-box__meta">
                <span className="thanks-ref-box__label">{t("thanks.orderNumber")}</span>
                <span className="thanks-ref-box__code" title={orderId}>
                  #{orderId}
                </span>
              </div>
              <button
                type="button"
                className={`thanks-ref-box__copy ${copied ? "thanks-ref-box__copy--copied" : ""}`}
                onClick={handleCopyOrderId}
                aria-label={copied ? t("thanks.copied") : t("thanks.copy")}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                <span>{copied ? t("thanks.copied") : t("thanks.copy")}</span>
              </button>
            </div>
          )}

          {/* Quick Meta Pills */}
          <div className="thanks-pills">
            <div className="thanks-pill">
              <Clock size={16} />
              <div>
                <span className="thanks-pill__label">{t("thanks.deliveryEstimate")}</span>
                <strong className="thanks-pill__val">{t("thanks.deliveryEstimateDays")}</strong>
              </div>
            </div>

            <div className="thanks-pill">
              {paymentMethod === "CreditCard" ? <CreditCard size={16} /> : <Banknote size={16} />}
              <div>
                <span className="thanks-pill__label">{t("thanks.paymentMethod")}</span>
                <strong className="thanks-pill__val">
                  {paymentMethod === "CreditCard" ? t("thanks.paymentCard") : t("thanks.paymentCod")}
                </strong>
              </div>
            </div>

            <div className="thanks-pill">
              <Truck size={16} />
              <div>
                <span className="thanks-pill__label">{t("product.freeDelivery")}</span>
                <strong className="thanks-pill__val">{t("thanks.deliveryCourier")}</strong>
              </div>
            </div>
          </div>
        </section>

        {/* Optional Order Summary Card (if fetched) */}
        {order && (
          <section className="thanks-summary-card">
            <div className="thanks-summary-card__header">
              <h2 className="thanks-summary-card__title">
                <PackageCheck size={18} />
                <span>{t("thanks.orderSummary")}</span>
              </h2>
              <span className="thanks-summary-card__count">
                {t("thanks.itemsCount").replace("{count}", order.orderItems?.length || 0)}
              </span>
            </div>

            <div className="thanks-summary-card__body">
              {order.shippingAddress && (
                <div className="thanks-summary-row">
                  <span className="thanks-summary-label">{t("thanks.shippingTo")}</span>
                  <span className="thanks-summary-value">
                    {order.shippingAddress.district?.name ? `${order.shippingAddress.district.name}, ` : ""}
                    {order.shippingAddress.city?.name || ""}
                  </span>
                </div>
              )}
              <div className="thanks-summary-row thanks-summary-row--total">
                <span className="thanks-summary-label">{t("thanks.totalAmount")}</span>
                <span className="thanks-summary-total">
                  {formatMoney(order.totalPrice, language)}
                </span>
              </div>
            </div>
          </section>
        )}

        {/* 4-Step Delivery Progress Tracker */}
        <section className="thanks-timeline-card">
          <h2 className="thanks-timeline-title">{t("thanks.timelineTitle")}</h2>
          <div className="thanks-timeline">
            <div className="thanks-timeline__step thanks-timeline__step--completed">
              <div className="thanks-timeline__node">
                <Check size={15} />
              </div>
              <div className="thanks-timeline__content">
                <span className="thanks-timeline__step-title">{t("thanks.step1")}</span>
                <span className="thanks-timeline__step-desc">{t("thanks.step1Desc")}</span>
              </div>
            </div>

            <div className="thanks-timeline__step thanks-timeline__step--active">
              <div className="thanks-timeline__node">
                <PackageCheck size={15} />
              </div>
              <div className="thanks-timeline__content">
                <span className="thanks-timeline__step-title">{t("thanks.step2")}</span>
                <span className="thanks-timeline__step-desc">{t("thanks.step2Desc")}</span>
              </div>
            </div>

            <div className="thanks-timeline__step">
              <div className="thanks-timeline__node">
                <Truck size={15} />
              </div>
              <div className="thanks-timeline__content">
                <span className="thanks-timeline__step-title">{t("thanks.step3")}</span>
                <span className="thanks-timeline__step-desc">{t("thanks.step3Desc")}</span>
              </div>
            </div>

            <div className="thanks-timeline__step">
              <div className="thanks-timeline__node">
                <ShieldCheck size={15} />
              </div>
              <div className="thanks-timeline__content">
                <span className="thanks-timeline__step-title">{t("thanks.step4")}</span>
                <span className="thanks-timeline__step-desc">{t("thanks.step4Desc")}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Actions / CTA Strip */}
        <div className="thanks-actions">
          <Link to="/shop" className="btn-primary thanks-btn-primary">
            <ShoppingBag size={17} />
            <span>{t("thanks.continueShopping")}</span>
            <ArrowIcon size={16} />
          </Link>

          {isAuthenticated && orderId ? (
            <Link to={`/account/orders/${orderId}`} className="btn-outline thanks-btn-outline">
              <span>{t("thanks.viewOrder")}</span>
              <ExternalLink size={15} />
            </Link>
          ) : isAuthenticated ? (
            <Link to="/account/orders" className="btn-outline thanks-btn-outline">
              <span>{t("thanks.viewAllOrders")}</span>
            </Link>
          ) : null}
        </div>

        {/* Help & Support Banner */}
        <div className="thanks-help-banner">
          <HelpCircle size={18} className="thanks-help-banner__icon" />
          <div className="thanks-help-banner__text">
            <span>{t("thanks.needHelp")}</span>
            <Link to="/contact" className="thanks-help-banner__link">
              {t("thanks.contactSupport")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
